import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PRODUCTS } from '../data/farmerData';
import { WaveRule, WaveDivider, CardWaveCut, PALETTE } from '../components/WaveTheme';
import './FarmersPage.css';

const DISPLAY_ORDER = [
  'OUO_GH_29_74',  // Ghee — Nirmala Rawat
  'OUO_BS_56_18',  // Black Soybean — Bhawana Rana
  'OUO_RC_37_92',  // Red Rice — Vandhana Rana
  'OUO_RR_15_62',  // Red Rajma — Keerthan Devi
  'OUO_TS_83_41',  // Tempering Spice — Ajbar Singh Rana
  'OUO_WR_47_23',  // White Rajma — Purushottam Negi
];

function FarmersPage() {
  const navigate = useNavigate();
  const [imagesReady, setImagesReady] = useState(false);

  const orderedProducts = DISPLAY_ORDER.map((id) => PRODUCTS[id]).filter(Boolean);

  useEffect(() => {
    if (orderedProducts.length === 0) {
      setImagesReady(true);
      return;
    }
    let loaded = 0;
    const total = orderedProducts.length;
    const done = () => {
      loaded += 1;
      if (loaded >= total) setImagesReady(true);
    };
    const loaders = orderedProducts.map((p) => {
      const img = new Image();
      img.onload = done;
      img.onerror = done;
      img.src = p.farmer.image;
      return img;
    });
    return () => {
      loaders.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!imagesReady) {
    return (
      <div className="wtn-loader">
        <div className="wtn-loader__inner">
          <span className="wtn-loader__mark">OUO</span>
          <div className="wtn-loader__wave">
            <WaveRule width={110} />
          </div>
          <p className="wtn-loader__text">Gathering our farmers</p>
          <div className="wtn-loader__dots" aria-hidden="true">
            <span /><span /><span />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wtn-farmers">

      {/* Nav */}
      <header className="wtn-nav">
        <div className="wtn-nav__inner">
          <div className="wtn-nav__brand" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <span className="wtn-nav__mark">OUO</span>
            <div>
              <p className="wtn-nav__name">Orang Utan Organics</p>
              <p className="wtn-nav__sub">Himalayan Traceability</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/')}
            className="wtn-btn wtn-btn--ghost wtn-nav__cta"
          >
            ← Home
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="wtn-section wtn-farmers-hero">
        <div className="wtn-section__inner">
          <div className="wtn-section__head">
            <span className="wtn-eyebrow">Our Farmers</span>
            <h1 className="wtn-h1">
              The people <span className="wtn-h1__accent">behind</span> every batch.
            </h1>
            <div className="wtn-h2-rule"><WaveRule width={110} /></div>
            <p className="wtn-section__sub">
              Meet the growers in the Uttarkashi cluster — small-hold farmers on
              high-altitude terraces, preserving native seed lines and traditional
              methods across generations.
            </p>
          </div>
        </div>
      </section>

      <WaveDivider height={72} palette={[PALETTE.CREAM_SOFT, PALETTE.CREAM]} />

      {/* Farmers grid */}
      <section className="wtn-section wtn-farmers-grid-section">
        <div className="wtn-section__inner">
          <div className="wtn-farmers__grid">
            {orderedProducts.map((p) => (
              <Link
                key={p.batch_id}
                to={`/batch/${encodeURIComponent(p.batch_id)}`}
                className="wtn-fcard"
                aria-label={`View batch for ${p.farmer.name} — ${p.product}`}
              >
                <div className="wtn-fcard__imgwrap">
                  <img
                    src={p.farmer.image}
                    alt={p.farmer.name}
                    className="wtn-fcard__img"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/600x750?text=Farmer+Photo';
                    }}
                  />
                  <span className="wtn-fcard__cat">{p.product}</span>
                  <CardWaveCut fill={PALETTE.CREAM_SOFT} />
                </div>
                <div className="wtn-fcard__body">
                  <h3 className="wtn-fcard__name">{p.farmer.name}</h3>
                  <p className="wtn-fcard__loc">
                    {p.village.name} · {p.village.district}, {p.village.state}
                  </p>
                  <div className="wtn-fcard__foot">
                    <span className="wtn-fcard__link">View Batch →</span>
                    <span className="wtn-fcard__batch">{p.batch_id}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="wtn-farmers__foot">
        <p>
          Powered by <span className="wtn-farmers__credit">TraceRoot</span>
        </p>
      </footer>

    </div>
  );
}

export default FarmersPage;
